import usePatientBriefModalOpener from 'components/Patients/PatientCard/PatientBriefTab/usePatientBriefModalOpener'
import usePatientBriefViewer from 'components/Patients/PatientGuidelines/PatientBrief/usePatientBriefViewer'
import usePracticeGuidelines from 'components/Patients/PatientGuidelines/PracticeGuidelines/usePracticeGuidelines'
import { AsyncStatus } from 'consts'
import { GuidelineCategories } from 'consts/hiToolsConsts'
import useAccountOwnerId from 'hooks/useAccountOwnerId'
import { keyBy } from 'lodash'
import { useCallback, useMemo } from 'react'
import { useFetchLatestPracticeGuidelinesQuery } from 'store/modules/CopilotControl'

/**
 * A hook that unifies the current patient's patient brief with the practice guidelines.
 *
 * @returns {{
 *  openPatientBriefModal: function,
 *  isLoading: boolean,
 *  hasPatientBrief: boolean,
 *  categories: Object<string, CopilotSettingsCategory>,
 * }}
 */
export default () => {
  const {
    loadingStatus,
    hasPatientBrief,
    categories: patientBriefCategories,
    latestPatientBrief
  } = usePatientBriefViewer()
  const { updatePatientBrief } = usePatientBriefModalOpener({ analyticsSource: 'patient-copilot-settings' })
  const isFetchingPatientBrief = useMemo(() => loadingStatus === AsyncStatus.Loading, [loadingStatus])

  const accountOwnerId = useAccountOwnerId()
  const { isFetching: isFetchingPracticeGuidelines, data: practiceGuidelines } = useFetchLatestPracticeGuidelinesQuery(
    { accountOwnerId },
    { skip: !accountOwnerId }
  )
  const parsedPracticeGuidelinesData = useMemo(
    () => JSON.parse(practiceGuidelines?.categories || '{}'),
    [practiceGuidelines]
  )
  const { categories: practiceGuidelinesCategories } = usePracticeGuidelines({
    categoriesData: parsedPracticeGuidelinesData
  })

  const unifiedCategories = useMemo(() => {
    // Create a unified categories dictionary that combines both practice guidelines and patient brief components.
    const practiceGuidelinesCategoriesDict = keyBy(practiceGuidelinesCategories, 'key')
    const patientBriefCategoriesDict = keyBy(patientBriefCategories, 'key')

    const categoriesDict = Object.values(GuidelineCategories).reduce((acc, categoryKey) => {
      const practiceGuidelineCategory = practiceGuidelinesCategoriesDict[categoryKey]
      const patientBriefCategory = patientBriefCategoriesDict[categoryKey]

      if (!patientBriefCategory) {
        return acc
      }

      const categoryData = {
        key: categoryKey,
        icon: patientBriefCategory.icon,
        title: patientBriefCategory.title,
        summary: patientBriefCategory?.summary,
        patientBrief: {
          component: patientBriefCategory?.component,
          data: latestPatientBrief.categories[categoryKey]
        },
        practiceGuidelines: {
          component: practiceGuidelineCategory?.component,
          data: parsedPracticeGuidelinesData[categoryKey]
        }
      }

      return {
        ...acc,
        [categoryKey]: categoryData
      }
    }, {})

    return categoriesDict
  }, [practiceGuidelinesCategories, patientBriefCategories, parsedPracticeGuidelinesData, latestPatientBrief])

  const isLoading = useMemo(
    () => isFetchingPatientBrief || isFetchingPracticeGuidelines,
    [isFetchingPatientBrief, isFetchingPracticeGuidelines]
  )

  const openPatientBriefModal = useCallback(() => {
    updatePatientBrief({
      patientBrief: {
        ...latestPatientBrief,
        categories: JSON.stringify(latestPatientBrief.categories)
      }
    })
  }, [updatePatientBrief, latestPatientBrief])

  return {
    openPatientBriefModal,
    isLoading,
    categories: unifiedCategories,
    hasPatientBrief
  }
}
